/**
 * バリデーション由来の要注意を**画面の現在値**から作る（#3358）。
 *
 * サマリーの要注意は result_data（OCR 時点）から判定するため、
 * 画面で入力を直しても保存するまで消えなかった。必須・文字数・文字種・
 * 正規表現・日付範囲などは画面でその場に直せる種類の指摘なので、
 * 「直したのに消えない」は使い勝手を大きく損なう。
 *
 * 判定そのものは既存の検証に委ねる（ここでは判定ロジックを持たない）。
 * - 単一項目: yup の errors（react-hook-form）
 * - 明細セル: validateArrayField
 *
 * 判定を二重に持つと、画面の赤字とサマリーで食い違う。
 * 既存の検証結果を ReviewIssue の形に**詰め替えるだけ**にしている。
 *
 * ⚠️ 確認ダイアログの件数（countReviewItemsForConfirm）も同じ考え方で
 *    現在値から数えている。条件を変えるときは揃えること。
 */
import { reviewIssueCode, reviewIssueSource } from '../../constants/enum';
import type { ReviewIssue } from '../../types/reviewIssue';

/** 判定に必要な項目の情報 */
export interface LiveValidationItem {
  /** 項目ID */
  id: string;
  /** 表示用の項目名 */
  parentValue: string;
  /** 有効な項目か */
  is_active?: boolean;
  /** 明細かどうか（type === 'array'） */
  type?: string;
  /** 明細の列 */
  children?: Array<{ id: string; childValue: string; is_active?: boolean }>;
}

/** 明細セル1件のエラー */
export interface LiveValidationCellError {
  /** 親項目ID */
  parentId: string;
  /** 子項目ID */
  childId: string;
  /** 行ID（表示位置ではない。#3283） */
  rowIndex: number;
  /** 表示する項目名 */
  label: string;
  /** エラー文言 */
  message: string;
}

/**
 * 現在値のバリデーションエラーを ReviewIssue の形で返す。
 *
 * @param items 項目一覧
 * @param singleErrors 単一項目の項目ID → エラー文言（yup の errors から作る）
 * @param cellErrors 明細セルのエラー
 */
export const buildLiveValidationIssues = (
  items: LiveValidationItem[],
  singleErrors: Map<string, string>,
  cellErrors: LiveValidationCellError[],
): ReviewIssue[] => {
  const issues: ReviewIssue[] = [];

  items.forEach(item => {
    if (item.is_active !== true) return;
    const message = singleErrors.get(item.id);
    if (message === undefined || message === '') return;

    issues.push({
      path: item.id,
      item_id: item.id,
      label: item.parentValue,
      source: reviewIssueSource.validation,
      // サマリーのグループ分けは source だけで決まるため、
      // ここでは種類を問わず代表コードを持たせる（#3262 の分類に合わせる）
      code: reviewIssueCode.required,
      message,
    });
  });

  cellErrors.forEach(cell => {
    issues.push({
      // 明細セルのパスは `親ID[行ID].子ID`（evaluateReviewIssues と同じ形）
      path: `${cell.parentId}[${cell.rowIndex}].${cell.childId}`,
      item_id: cell.childId,
      row_index: cell.rowIndex,
      label: cell.label,
      source: reviewIssueSource.validation,
      code: reviewIssueCode.required,
      message: cell.message,
    });
  });

  return issues;
};

/**
 * 保存済みの要注意から、バリデーション由来を取り除く。
 *
 * 取り除いた分は buildLiveValidationIssues が現在値で作り直す。
 * 両方残すと、直した後も古い判定がサマリーに残って消えない。
 *
 * reviewChoice は別扱い（withoutStaleReviewChoices が受け持つ）。
 * こちらは yup に相当する検証が無く、現在値の作り方も違うため。
 */
export const withoutStaleValidationIssues = (issues: ReviewIssue[]): ReviewIssue[] =>
  issues.filter(
    issue =>
      issue.source !== reviewIssueSource.validation || issue.code === reviewIssueCode.reviewChoice,
  );
